/**
 * RevenueCat / Apple StoreKit bridge for the iOS Capacitor build.
 *
 * We only initialize on native iOS — the web bundle never imports the
 * plugin because the top-level import is done via dynamic import() gated
 * on isNativeApp() + platform === "ios". This keeps the web SPA free of
 * native module resolution warnings.
 *
 * Public SDK key comes from GET /api/apple/config so we don't need to
 * bake it into every frontend build.
 */
import { isNativeApp } from "@/lib/platform";
import { apiFetch } from "@/lib/supabase";

let _configured = false;
let _rcModule = null;
let _config = null;

async function loadPlugin() {
  if (_rcModule) return _rcModule;
  _rcModule = await import("@revenuecat/purchases-capacitor");
  return _rcModule;
}

function isIOS() {
  if (!isNativeApp()) return false;
  const cap = typeof window !== "undefined" ? window.Capacitor : null;
  try { return cap?.getPlatform?.() === "ios"; } catch { return false; }
}

export async function getIAPConfig() {
  if (_config) return _config;
  try {
    _config = await apiFetch("/apple/config");
  } catch {
    _config = { enabled: false, ios_api_key: "", product_to_plan: {} };
  }
  return _config;
}

/**
 * Configure the RevenueCat SDK. Safe to call multiple times; only runs
 * the underlying `Purchases.configure` once per app session.
 *
 * @param {string} supabaseUserId - Supabase auth user id (session.user.id)
 */
export async function configureRevenueCat(supabaseUserId) {
  if (!isIOS() || !supabaseUserId) return { ready: false, reason: "not_ios_or_no_user" };
  const cfg = await getIAPConfig();
  if (!cfg.enabled || !cfg.ios_api_key) return { ready: false, reason: "config_disabled" };
  const { Purchases } = await loadPlugin();
  if (_configured) {
    try { await Purchases.logIn({ appUserID: supabaseUserId }); } catch { /* ignore */ }
    return { ready: true };
  }
  await Purchases.configure({ apiKey: cfg.ios_api_key, appUserID: supabaseUserId });
  _configured = true;
  return { ready: true };
}

/** Fetch RevenueCat offerings; returns { packages: [...] } or { packages: [] }. */
export async function getOfferings() {
  if (!isIOS()) return { packages: [] };
  const { Purchases } = await loadPlugin();
  try {
    const res = await Purchases.getOfferings();
    const current = res?.current || res?.offerings?.current;
    const packages = current?.availablePackages || [];
    return { packages, offering: current };
  } catch (e) {
    return { packages: [], error: e?.message || "offerings_failed" };
  }
}

/** Purchase a RevenueCat package. Returns { customerInfo, productId, entitlements }. */
export async function purchasePackage(pkg) {
  const { Purchases } = await loadPlugin();
  const result = await Purchases.purchasePackage({ aPackage: pkg });
  const customerInfo = result?.customerInfo;
  const productId = pkg?.product?.identifier || pkg?.storeProduct?.identifier;
  return {
    customerInfo,
    productId,
    entitlements: customerInfo?.entitlements?.active || {},
  };
}

/** Trigger Apple's Restore Purchases flow. */
export async function restorePurchases() {
  const { Purchases } = await loadPlugin();
  const result = await Purchases.restorePurchases();
  return result?.customerInfo || null;
}

/**
 * Open Apple's native Manage Subscriptions sheet for the current App Store
 * account. Users can Change plan / Cancel renewal / See renewal date /
 * Rejoin all from this system UI. Falls back to opening the Apple
 * subscriptions URL directly if the SDK method isn't available in this
 * plugin build (older versions).
 */
export async function showManageSubscriptions() {
  const { Purchases } = await loadPlugin();
  if (typeof Purchases.showManageSubscriptions === "function") {
    try { await Purchases.showManageSubscriptions(); return { ok: true }; }
    catch (e) { /* fall through to URL fallback */ }
  }
  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({ url: "https://apps.apple.com/account/subscriptions" });
    return { ok: true, fallback: true };
  } catch (e) {
    return { ok: false, error: e?.message || "manage_failed" };
  }
}

/** Read cached CustomerInfo (fast, no network). */
export async function getCustomerInfo() {
  if (!isIOS()) return null;
  const { Purchases } = await loadPlugin();
  const res = await Purchases.getCustomerInfo();
  return res?.customerInfo || null;
}

/**
 * Extract renewal / expiration + cancellation state from CustomerInfo for
 * the currently-active self-service entitlement. Returns null if no
 * matching entitlement is active.
 */
export function extractRenewalStatus(customerInfo) {
  const active = customerInfo?.entitlements?.active || {};
  const entKey = ["starter", "professional", "business"].find((k) => active[k]);
  if (!entKey) return null;
  const e = active[entKey];
  return {
    entitlement: entKey,
    expiresDate: e.expirationDate || e.expiration_date || null,
    willRenew: e.willRenew !== false,
    productIdentifier: e.productIdentifier || e.product_identifier || null,
  };
}

export { isIOS };
